import { useDialog } from '@ics-portal/guardian';
import { type ReactNode, useCallback, useMemo } from 'react';
import SearchComponentsDialog, {
    type SearchComponentsDialogProps,
} from '@components/NewComponentCreateRequestTrigger/components/SearchComponentsDialog/SearchComponentsDialog';
import { ComponentCreateRequestRequestPriority } from '@gql/graphql';
import { useNewComponentRequestActions } from '@hooks/NewComponentRequests/useNewComponentRequestActions';
import { useFormHandler } from '@tra/hooks';
import {
    NewComponentRequestForm,
    type NewComponentRequestFormValue,
} from './components/NewComponentRequestForm';

interface NewComponentRequestDialogProps {
    searchComponentsDialogProps?: SearchComponentsDialogProps;
}

export function NewComponentRequestDialog(
    props: NewComponentRequestDialogProps,
): ReactNode {
    const { searchComponentsDialogProps: dialogProps } = props;
    const { form: formDialog, close: closeDialog } = useDialog();
    const { create } = useNewComponentRequestActions();

    const close = useMemo(() => {
        if (!dialogProps) {
            return closeDialog;
        }

        return () => {
            formDialog({
                title: 'Search and select component',
                content: <SearchComponentsDialog {...dialogProps} />,
                buttons: {
                    submitLabel: 'Use selected component',
                },
            });
        };
    }, [closeDialog, formDialog, dialogProps]);

    const onSubmit = useCallback(
        async (data: Partial<NewComponentRequestFormValue>) => {
            await create(data);

            if (!dialogProps) {
                close();
                return;
            }

            formDialog({
                title: 'Search and select component',
                content: <SearchComponentsDialog {...dialogProps} />,
                buttons: {
                    submitLabel: 'Use selected component',
                },
            });
        },
        [],
    );

    const { form } = useFormHandler<NewComponentRequestFormValue>({
        defaultValues: {
            requestPriority: ComponentCreateRequestRequestPriority.Low,
        },
        onSubmit,
        loading: false,
        disableInvalidSubmit: true,
        closeAfterSubmit: false,
    });

    return <NewComponentRequestForm header="Component details" form={form} />;
}
